/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import _ from 'lodash';
import { AssetSymbol } from '@tradingblock/types';
import { TradingViewChartOptions, createTradingViewChart, isDailyChartResolution } from '@tradingblock/tradingview';
import { IChartingLibraryWidget, TimeFrameItem } from '@tradingblock/tradingview/dist/charting_library';
import { useBlockActions, useBlockData } from '../../components/blocks/BlockState';
import { useLastTradePricesOrUndefined } from '../../hooks/useFeedQuote';
import { Colors } from '../../constants';
import { PriceChartState } from './state/PriceChartState';
import { PriceChartSettingsKey, usePriceChartSettings } from './state/PriceChartSettings';
import { useTradingviewDatafeed } from './hooks/useTradingviewDatafeed';
import { getDrawingOverrides } from './options/drawingOverrides';
import { getOverrides } from './options/overrides';
import { timeZone } from '@tradingblock/api';
import { EntityInfo, ResolutionString } from '@tradingblock/tradingview/src/charting_library';
import { useDispatch } from 'react-redux';
import { QuoteDataActions } from '../../data/global/actions';
import { useQuoteActions } from '../../hooks/useQuoteData';
import { isMarketOpenSelector, useStateSelector } from '../../data/global/dataSelectors';

interface PriceChartGraphProps {
  symbol: AssetSymbol;
  containerId: string;
}

const DefaultInterval = '5' as ResolutionString;

// chartType enum
export enum ChartType {
  Bar = 0,
  Candle = 1,
  Line = 2,
  Area = 3,
  Renko = 4,
  Kagi = 5,
  PnF = 6,
  LineBreak = 7,
  HeikinAshi = 8,
  HollowCandle = 9,
  Baseline = 10,
  HiLo = 12,
  Column = 13,
}

// function to determine if chart type is heikin ashi
const isHeikinAshi = (chartType: ChartType) => {
  return chartType === ChartType.HeikinAshi;
};

export const PriceChartGraph: React.FC<PriceChartGraphProps> = (props) => {
  const { isLoaded, savedData, interval, showLeftToolbar, chartType, symbol } = useBlockData<PriceChartState>();
  const { setField: set } = useBlockActions<PriceChartState>();

  const saveChartData = useCallback((key: PriceChartSettingsKey, data: any) => {
    set(key, data, { persist: true });
  }, []);
  const blockSymbol = symbol && symbol.rootSymbol;

  // don't render chart until block data has loaded
  if (!savedData && !isLoaded) {
    console.error('not rendering chart because data doesnt exist OR isLoaded is false', { isLoaded, savedData });
    return null;
  }

  return (
    <PriceChartGraphComponent
      {...props}
      savedData={savedData}
      interval={interval}
      showLeftToolbar={showLeftToolbar}
      saveData={saveChartData}
      chartType={chartType}
      blockSymbol={blockSymbol}
    />
  );
};

interface PriceChartGraphComponentProps
  extends PriceChartGraphProps,
    Pick<PriceChartState, 'savedData' | 'interval' | 'showLeftToolbar' | 'chartType'> {
  saveData: (key: PriceChartSettingsKey, data: any) => void;
  blockSymbol?: string;
}

const PriceChartGraphComponent: React.FC<PriceChartGraphComponentProps> = (props) => {
  const { containerId, savedData, interval, showLeftToolbar, chartType, saveData, blockSymbol } = props;
  const { symbol, provider } = props.symbol;

  const chartRef = useRef<IChartingLibraryWidget>();
  const datafeed = useTradingviewDatafeed();
  const { debug } = usePriceChartSettings();
  // Include block symbol: datafeed.symbols can be [] briefly while TV unsubscribes/resubscribes on timeframe change,
  // but Redux still has quotes for the selected ticker (header keeps updating).
  const symbols = _.uniq([...datafeed.symbols, symbol].filter((s): s is string => typeof s === 'string' && s.length > 0));
  const dispatch = useDispatch();
  const { addQuote } = useQuoteActions();
  const [chartTypeValue, setChartTypeValue] = React.useState<ChartType>(chartType || ChartType.Candle);
  const [chartInterval, setChartInterval] = React.useState<string>(interval || DefaultInterval);
  const [studies, setStudies] = React.useState<EntityInfo[]>([]);

  useEffect(() => {
    if (symbols.length > 0) {
      dispatch(QuoteDataActions.requestQuote({ symbol: symbols }));
      addQuote(symbols);
    }
  }, [datafeed, symbols.length, dispatch]);

  const visibilityChartSync = function () {
    if (document.visibilityState === 'visible') {
      if (!chartRef || !chartRef.current || !datafeed) {
        return;
      }

      datafeed.resetCache();

      if (chartRef.current.activeChart()) {
        chartRef.current.activeChart().resetData();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('visibilitychange', visibilityChartSync);
    return () => {
      document.removeEventListener('visibilitychange', visibilityChartSync);
    };
  }, [chartRef, datafeed]);

  const timeFrames = useMemo<TimeFrameItem[]>(
    () => [
      { text: '3m', resolution: '60' as ResolutionString, description: '3 Months' },
      { text: '1m', resolution: '30' as ResolutionString, description: '1 Month' },
      { text: '5d', resolution: '5' as ResolutionString, description: '5 Days' },
      { text: '1d', resolution: '1' as ResolutionString, description: '1 Day' },
    ],
    []
  );

  /** Last symbol we applied with setSymbol; interval-only updates use setResolution to preserve timeframe toolbar range. */
  const lastAppliedFullSymbolRef = useRef<string | undefined>(undefined);
  /** TradingView active resolution — block `interval` can lag behind TV during timeframe clicks; quote routing must match TV caches. */
  const [chartLiveResolution, setChartLiveResolution] = React.useState<ResolutionString | undefined>(undefined);
  /** ISubscription.subscribe returns void; cleanup is unsubscribe(null, sameHandler). */
  const intervalChangeCleanupRef = useRef<(() => void) | undefined>(undefined);

  const graphSettings = useMemo<TradingViewChartOptions>(() => {
    return {
      locale: 'en',
      libraryPath: '/charting_library/',
      clientId: 'tradingview.com',
      userId: 'public_user_id',
      disabled_features: [
        'use_localstorage_for_settings',
        'header_screenshot',
        'header_symbol_search',
        'go_to_date',
        'popup_hints',
      ],
      create_volume_indicator_by_default: true, // Ensures volume is included
      enabled_features: ['move_logo_to_main_pane', 'hide_left_toolbar_by_default'],
      fullscreen: false,
      studiesOverrides: {},
      datafeed,
      debug: false,
      autosize: true,
      symbol: `${provider}:${symbol}`,
      savedData,
      interval: (interval as ResolutionString) || (DefaultInterval as ResolutionString),
      timezone: timeZone,
      theme: 'Dark',
      style: '1',
      custom_css_url: `${process.env.PUBLIC_URL}/styles/tradingview.css`,
      enable_publishing: false,
      allow_symbol_change: false,
      hide_top_toolbar: true,
      save_image: false,
      containerId,
      time_frames: timeFrames,

      // ✅ **Key Fix: Enforce separation between price and volume bars**
      rightPriceScale: {
        scaleMargins: {
          top: 0.1, // Ensures space above price
          bottom: 0.35, // **Pushes price bars away from volume bars**
        },
      },

      // ✅ **Ensure volume bars have their own independent pane**
      overrides: {
        ...getOverrides({ primaryColor: Colors.primary }),
        ...getDrawingOverrides(),
        volumePaneSize: 'large', // Forces larger space for volume
        'scalesProperties.showLeftScale': false, // Hide left scale for a cleaner look
        'mainSeriesProperties.priceScaleId': 'right', // Ensures price scale is on the right
        'paneProperties.topMargin': 10, // Ensures space above price bars
        'paneProperties.bottomMargin': 30, // Pushes price bars away from volume
      },

      // ✅ **Enforce Auto-Scaling**
      autoscale: true,
    };
    // Intentionally omit savedData, symbol, interval, provider from deps: they update often (e.g. auto-save)
    // and would recreate graphSettings every render → chart useEffect remounts the widget in a loop.
    // Symbol/interval are applied via setSymbol effects; initial snapshot here is enough for first paint.
  }, [datafeed, debug, containerId, timeFrames]);

  // useLayoutEffect(() => {
  //   if (chartRef.current) {
  //     chartRef.current.changeTheme(theme);
  //   }
  // }, [theme]);

  useLayoutEffect(() => {
    if (chartRef && chartRef.current) {
      chartRef.current.onChartReady(() => {
        if (chartRef && chartRef.current !== undefined) {
          chartRef.current.hideAllDrawingTools().setValue(!showLeftToolbar);
        }
      });
    }
  }, [showLeftToolbar]);

  // sync chart type on change
  useEffect(() => {
    if (chartType && chartType !== chartTypeValue && chartRef && chartRef.current !== undefined) {
      chartRef.current.onChartReady(() => {
        visibilityChartSync();
        setChartTypeValue(chartType);
      });
    }
  }, [chartType, chartTypeValue, chartRef.current]);

  // Keep local chartInterval in sync for any UI that reads it; do not call visibilityChartSync/resetData here — that
  // wiped the visible range after timeframe toolbar clicks (required two clicks to see the preset as active).

  useEffect(() => {
    const chart = document.querySelectorAll('.block-price-chart');
    if (interval && chartRef && chartRef.current !== undefined && chart.length > 0) {
      chartRef.current.onChartReady(() => {
        setChartInterval(interval);
        chart.forEach((element: Element) => {
          element.classList.add('chart-disabled');
          const timer = setTimeout(() => {
            element.classList.remove('chart-disabled');
          }, 3000);

          return () => clearTimeout(timer);
        });
      });
    }
  }, [interval, chartInterval, chartRef.current, chartTypeValue, chartType, chartRef]);

  // Single source of truth: setSymbol reloads series and resets visible range; use setResolution when only interval
  // changes so bottom timeframe buttons (3m/1m/5d/1d) keep their zoom in one click.
  useEffect(() => {
    const targetSymbol = blockSymbol || symbol;
    if (!chartRef.current || !targetSymbol || provider === undefined) {
      return;
    }

    const fullSymbol = `${provider}:${targetSymbol}`;
    const intervalStr = (interval as ResolutionString) || DefaultInterval;

    chartRef.current.onChartReady(() => {
      const w = chartRef.current;
      if (!w) {
        return;
      }
      const ac = w.activeChart();
      if (!ac) {
        return;
      }

      const symbolChanged = lastAppliedFullSymbolRef.current !== fullSymbol;

      if (symbolChanged) {
        console.log('[PriceChartGraph] setSymbol', fullSymbol, intervalStr);
        w.setSymbol(fullSymbol, intervalStr, () => {});
        lastAppliedFullSymbolRef.current = fullSymbol;
        return;
      }

      const tvRes = ac.resolution();
      if (tvRes !== intervalStr) {
        console.log('[PriceChartGraph] setResolution', intervalStr, 'was', tvRes);
        ac.setResolution(intervalStr, () => {});
      }
    });
  }, [blockSymbol, symbol, provider, interval, savedData]);

  useEffect(() => {
    if (chartRef && chartRef.current === undefined) {
      console.warn('PriceChartGraph :: creating', symbol, 'chart with data ', graphSettings.savedData);
      chartRef.current = createTradingViewChart(graphSettings, saveData);
      chartRef.current.onChartReady(() => {
        const w = chartRef.current;
        if (!w) {
          return;
        }
        const ac = w.activeChart();
        if (!ac) {
          return;
        }
        setChartLiveResolution(ac.resolution());
        const prevCleanup = intervalChangeCleanupRef.current;
        if (prevCleanup) {
          prevCleanup();
        }
        const intervalSub = ac.onIntervalChanged();
        const onIntervalHandler = () => {
          setChartLiveResolution(ac.resolution());
        };
        intervalSub.subscribe(null, onIntervalHandler);
        intervalChangeCleanupRef.current = () => {
          intervalSub.unsubscribe(null, onIntervalHandler);
        };
      });
    }

    return () => {
      const cleanup = intervalChangeCleanupRef.current;
      if (cleanup) {
        cleanup();
      }
      intervalChangeCleanupRef.current = undefined;
      setChartLiveResolution(undefined);
      if (chartRef && chartRef.current !== undefined) {
        chartRef.current.remove();
        chartRef.current = undefined;
        lastAppliedFullSymbolRef.current = undefined;
      }
    };
  }, [graphSettings, saveData, chartRef]);

  // Fetch last trade prices of all symbols involved in the chart and update the datafeed
  // If the chart is a heikin ashi chart, TimeReceived will not include seconds only hour and minutes
  const resolutionForQuotes = (chartLiveResolution || interval || DefaultInterval) as string;
  const lastPrices = useLastTradePricesOrUndefined(symbols, isHeikinAshi(chartType || 1), resolutionForQuotes);

  const hasQuotePayload =
    lastPrices &&
    Object.values(lastPrices).some(
      (q): q is { Symbol: string } =>
        q != null && typeof q === 'object' && typeof (q as { Symbol?: string }).Symbol === 'string'
    );

  const intervalCheck = isDailyChartResolution(resolutionForQuotes);
  const marketOpen = useStateSelector((s) => isMarketOpenSelector(s));
  // Daily: always apply quotes so the current session's daily bar can update. Intraday: skip only when market is explicitly closed (undefined schedule still streams to avoid a dead chart).
  const marketIsOpen = intervalCheck ? true : marketOpen !== false;

  useEffect(() => {
    if (hasQuotePayload && marketIsOpen) {
      datafeed.onQuoteChange(lastPrices, isHeikinAshi(chartType || 1), chartLiveResolution);
    }
  }, [lastPrices, hasQuotePayload, datafeed, marketIsOpen, chartType, chartLiveResolution]);

  // Need to save the chart data whenever a study is created or removed from the chart
  // previously, these updates were delayed by up to 5 seconds, which was causing some confusion as to why removed studies were still showing up
  // or why newly created studies were not showing up after a refresh or page navigation
  useEffect(() => {
    // We're checking if `chartRef` is defined and if it points to a current chart.
    if (chartRef && chartRef.current !== undefined) {
      // If `chartRef.current` is defined, we then wait for the chart to be ready.
      chartRef.current.onChartReady(() => {
        // Once the chart is ready, we check again if `chartRef.current` is still defined.
        // This check is necessary because the reference may have changed while we were waiting for the chart to be ready.
        if (chartRef && chartRef.current !== undefined && chartRef.current.activeChart() !== undefined) {
          // If `chartRef.current` is still defined, we retrieve all studies from the active chart.
          const currentStudies = chartRef.current.activeChart().getAllStudies();
          // We then store these studies in the state.
          setStudies(currentStudies);
          // We compare the new list of studies with the old list.
          // If they are not equal, it means the studies have changed.
          if (!_.isEqual(currentStudies, studies)) {
            // If the studies have changed, we update the state with the new studies,
            // and then we save the chart's current state.
            setStudies(currentStudies);
            chartRef.current.save((savedData: any) => {
              // The `save` method's callback receives the chart's saved state,
              // which we then store in our mongo database.
              saveData('savedData', savedData);
            });
          }
        }
      });
    }
  }, [chartRef.current, lastPrices]); // The effect depends on `chartRef.current` and `lastPrices`.

  return (
    <>
      <div className="tradingview-widget-container">
        <div id={props.containerId} className="tradingviewChart" />
      </div>
    </>
  );
};
